/**
 * Waiting for a generation whose result never reached the browser.
 *
 * `generateImage` writes the GCS object, saves the StudioImage row and deducts
 * the credit before it answers, and nothing cancels a Cloud Run request once
 * the client is gone. So a callable that fails without a response has usually
 * not failed at all: the image is still on its way to the database.
 *
 * Rather than report that as an error and let the row surface on the next
 * manual reload, the call waits for it. The wait costs nothing the caller was
 * not already spending, because the generation it is waiting on is the one it
 * had asked for. Only the case where the image never arrives is slower than
 * before, and that case is bounded by the backend's own timeout.
 */

import { reportError } from "../utils/errorReporting";

/**
 * The `generateImage` function's own timeout, mirrored from its
 * `timeoutSeconds: 540` in functions/src/ai/handler.ts. It is the latest the
 * backend can still be writing a row for a request the browser already gave
 * up on, so it bounds how long there is any point in waiting.
 */
export const GENERATE_IMAGE_TIMEOUT_MS = 540_000;

/**
 * Whether a failed callable means "no answer arrived", as opposed to a
 * rejection the backend deliberately sent.
 *
 * Only the former can leave a finished image behind. The Firebase SDK cannot
 * tell a dropped connection from a crashed backend — it turns every fetch
 * rejection into HTTP status 0 — so both surface as `functions/internal` with
 * the bare code as the message. A body from our own handler carries a real
 * message instead, and that path already recorded the request as failed.
 *
 * @param {unknown} error The error a generation call rejected with.
 * @return {boolean} True when no response body reached the browser.
 */
export function isLostGenerationResponse(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code !== "string") return false;
  if (code === "functions/deadline-exceeded") return true;
  if (code !== "functions/internal") return false;
  // No parseable body: the SDK defaults the description to the bare code.
  return error.message === "internal" || error.message === "";
}

/**
 * Whether the request could plausibly have reached the backend at all.
 *
 * A browser that is offline fails the fetch exactly the way a dropped
 * connection does, but nothing was sent: no image is being written and no
 * credit was spent. Waiting would hold the user for minutes and then tell
 * them to go looking for a result that never existed.
 *
 * One-directional on purpose. Offline is conclusive; online proves nothing,
 * since the flag only tracks the interface and not whether packets arrive.
 *
 * @return {boolean} False only when the browser knows it was offline.
 */
export function mayHaveReachedBackend(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** A gallery row as the recovery needs to see it. */
export interface GalleryImageRow {
  id: string;
  productId: string;
  imageUrl: string;
  generationSettings: string | null;
  createdAt: string;
}

/**
 * Gaps between lookups, in ms. Tight at the start because most results land
 * within a minute of the connection dropping, then widening so a long wait
 * costs a handful of queries rather than one every few seconds.
 */
export const POLL_DELAYS_MS = [
  5_000, 10_000, 15_000, 20_000, 30_000, 45_000, 60_000, 60_000, 90_000, 90_000,
  120_000,
];

/**
 * Where the client's own request identifier rides into the stored row.
 *
 * The backend merges the settings it is given and keeps keys it does not know
 * about, so this reaches `StudioImage.generationSettings` without a schema or
 * backend change. It is what makes a recovered row provably *this* request's
 * result rather than whatever appeared most recently.
 */
export const CLIENT_REQUEST_ID_KEY = "clientRequestId";

export interface AwaitRecoveredGenerationDeps {
  projectId: string;
  /** The id this call stamped onto its own request. */
  requestId: string;
  /** How much longer the backend could still be working. */
  deadlineMs: number;
  /** Reads the project's gallery rows. */
  listImages: (projectId: string) => Promise<GalleryImageRow[]>;
  /** Clock injection point for tests. Defaults to Date.now. */
  now?: () => number;
  /** Sleep injection point for tests. Defaults to a real timer. */
  wait?: (ms: number) => Promise<void>;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Polls the gallery until the image this request produced appears.
 *
 * @param {AwaitRecoveredGenerationDeps} deps Lookup target and injection points.
 * @return {Promise<GalleryImageRow | null>} The row, or null if none arrived.
 */
export async function awaitRecoveredGeneration({
  projectId,
  requestId,
  deadlineMs,
  listImages,
  now = Date.now,
  wait = sleep,
}: AwaitRecoveredGenerationDeps): Promise<GalleryImageRow | null> {
  // A lookup failure is not an answer. Keep the window open, because the
  // image may still be landing while Data Connect is briefly unreachable.
  // But a window in which *every* lookup failed is indistinguishable from
  // one where the image never arrived, so that case is reported rather than
  // left to look like an ordinary miss.
  let reads = 0;
  let readFailures = 0;
  let lastReadError: unknown = null;
  const lookup = async () => {
    reads += 1;
    const rows = await listImages(projectId).catch((error: unknown) => {
      readFailures += 1;
      lastReadError = error;
      return null;
    });
    return rows ? matchRequest(rows, requestId) : null;
  };

  const giveUp = (): null => {
    if (reads > 0 && readFailures === reads) {
      reportError(lastReadError, {
        context: "awaitRecoveredGeneration",
        projectId,
        requestId,
        reads,
      });
    }
    return null;
  };

  // Measured against the wall clock, not the sum of the sleeps. Each lookup
  // is a network call, so charging only the sleeps to the budget would hold
  // the caller well past the point where the backend could still be running,
  // which is the whole reason this wait is safe to make them sit through.
  const startedAt = now();
  const elapsed = () => now() - startedAt;

  for (const delayMs of POLL_DELAYS_MS) {
    const found = await lookup();
    if (found) return found;

    const remainingMs = deadlineMs - elapsed();
    if (remainingMs <= 0) return giveUp();
    // Clamped, so the last look lands on the deadline rather than before it.
    // The backend may write the row at any moment up to its own timeout, and
    // stopping a gap early would silently lose exactly those late results.
    await wait(Math.min(delayMs, remainingMs));
  }

  return (await lookup()) ?? giveUp();
}

/**
 * The row this request produced, identified rather than guessed.
 *
 * Matching on "newest for the product" would hand back another generation's
 * image whenever two are in flight at once, for instance from two tabs. An
 * unattributable row is never claimed, so an unexpected shape costs a
 * recovery rather than showing the user someone else's result.
 *
 * @param {GalleryImageRow[]} rows Candidate rows.
 * @param {string} requestId The id this call stamped onto its request.
 * @return {GalleryImageRow | null} The matching row, or null.
 */
function matchRequest(
  rows: GalleryImageRow[],
  requestId: string
): GalleryImageRow | null {
  return (
    rows.find((r) => requestIdOf(r.generationSettings) === requestId) ?? null
  );
}

/**
 * Reads the client request id back out of a row's stored settings.
 *
 * @param {string | null} raw Serialized settings from the gallery row.
 * @return {string | null} The id, or null when absent or unreadable.
 */
function requestIdOf(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const id = (parsed as Record<string, unknown>)[CLIENT_REQUEST_ID_KEY];
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}
